/* StoriesMine — Instagram-style personal Stories viewer.
   - fetches /api/stories/me
   - full-screen player limited to caller's stories
   - each story has a delete button
   - "+" bottom button to open the uploader
   - Props:
     • onClose: mandatory
     • uploaderOnly: if true, skip player and open uploader directly
     • onOpenUploader: parent-triggered upload open (from empty state)
*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Trash2, Plus, Eye, X, Loader2, Volume2, VolumeX, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { hapticTap } from "@/lib/haptics";
import { StoryUploader } from "./Stories";

function buildSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function StoriesMine({ onClose, uploaderOnly, onOpenUploader }) {
  const [stories, setStories] = useState([]);
  const [quota, setQuota] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [showUploader, setShowUploader] = useState(!!uploaderOnly);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stories/me");
      setStories(data.items || []);
      setQuota(data.quota || null);
      setIdx(0);
    } catch (e) {
      console.warn("stories/me", formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Progress timer
  useEffect(() => {
    setProgress(0);
    const s = stories[idx];
    if (!s || showUploader) return;
    const isImage = s.media_type === "image";
    const dur = isImage ? 5 : Math.min(Number(s.duration_s || 10), 10);
    const start = Date.now();
    const tick = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / (dur * 1000));
      setProgress(p);
      if (p >= 1) {
        clearInterval(tick);
        setIdx((i) => (i + 1 < stories.length ? i + 1 : i));
      }
    }, 80);
    return () => clearInterval(tick);
  }, [idx, stories, showUploader]);

  const current = stories[idx];

  const del = async () => {
    if (!current) return;
    if (!window.confirm("Vuoi eliminare questa storia?")) return;
    setDeleting(true);
    try {
      await api.delete(`/stories/${current.id}`);
      hapticTap();
      toast.success("Storia eliminata");
      const next = stories.filter((s) => s.id !== current.id);
      setStories(next);
      setIdx(Math.max(0, Math.min(idx, next.length - 1)));
      if (next.length === 0) setShowUploader(false);
    } catch {
      toast.error("Impossibile eliminare la storia");
    } finally {
      setDeleting(false);
    }
  };

  const onDragEnd = (_, info) => {
    if (info.offset.y < -60 && idx + 1 < stories.length) setIdx(idx + 1);
    else if (info.offset.y > 60 && idx > 0) setIdx(idx - 1);
  };

  // If uploaderOnly OR player empty → straight to uploader
  const shouldShowUploader = showUploader || (!loading && stories.length === 0);

  return createPortal((
    <div
      data-testid="stories-mine"
      className="fixed inset-0 z-[2000] bg-black text-white select-none touch-none"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {!shouldShowUploader && (
        <>
          {/* Progress bars */}
          <div className="absolute inset-x-0 top-0 z-30 px-3 pt-3 pointer-events-none">
            <div className="flex items-center gap-1 mb-2">
              {stories.map((_, i) => (
                <div key={i} className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
                  <div
                    className="h-full bg-white"
                    style={{
                      width: i < idx ? "100%" : i === idx ? `${progress * 100}%` : "0%",
                      transition: i === idx ? "width 80ms linear" : "none",
                    }}
                  />
                </div>
              ))}
              {stories.length === 0 && <div className="flex-1 h-[3px] rounded-full bg-white/20" />}
            </div>

            <div className="flex items-center justify-between pointer-events-auto">
              <button
                onClick={() => { hapticTap(); onClose(); }}
                data-testid="stories-mine-back"
                className="h-10 w-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center"
                aria-label="Chiudi"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-2">
                <div className="orio-wordmark-grad h-5 w-[54px]" style={{ filter: "brightness(2)" }} />
                <span className="text-[11px] tracking-[0.22em] uppercase text-white/70">Le tue storie</span>
              </div>

              <button
                onClick={del}
                disabled={!current || deleting}
                data-testid="stories-mine-delete"
                className="h-10 w-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center disabled:opacity-40"
                aria-label="Elimina"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Player */}
          <div className="absolute inset-0">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-white/70" />
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {current && (
                  <motion.div
                    key={current.id}
                    drag="y"
                    dragConstraints={{ top: 0, bottom: 0 }}
                    dragElastic={0.35}
                    onDragEnd={onDragEnd}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.28 }}
                    className="absolute inset-0 flex items-center justify-center"
                    onClick={() => setMuted((m) => !m)}
                  >
                    {current.thumb_url && (
                      <img src={current.thumb_url} alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-40"
                        style={{ filter: "blur(24px) saturate(1.1)" }} />
                    )}
                    {current.media_type === "image" ? (
                      <img key={current.video_url} src={buildSrc(current.video_url)}
                        alt="" className="relative z-10 max-h-full max-w-full object-contain"
                        style={{ background: "black" }} />
                    ) : (
                      <video
                        ref={videoRef}
                        key={current.video_url}
                        src={buildSrc(current.video_url)}
                        poster={current.thumb_url}
                        autoPlay loop muted={muted} playsInline
                        className="relative z-10 max-h-full max-w-full object-contain"
                        style={{ background: "black" }}
                      />
                    )}
                    {current.media_type !== "image" && (
                      <div className="absolute top-24 right-4 z-20 h-9 w-9 rounded-full bg-black/45 backdrop-blur flex items-center justify-center pointer-events-none">
                        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>

          {/* Bottom stats + views + add */}
          {current && (
            <div className="absolute inset-x-0 z-30 px-4 flex items-end justify-between gap-3"
              style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}>
              <div className="flex items-center gap-3 px-3 py-2 rounded-full bg-black/50 backdrop-blur">
                <div className="flex items-center gap-1 text-[12px] font-semibold">
                  <Eye className="h-3.5 w-3.5" /> {current.views_count || 0}
                </div>
                <div className="text-[12px]">❤️ {current.reactions_person || 0}</div>
                <div className="text-[12px]">🐾 {current.reactions_dog || 0}</div>
                {current.location_label && (
                  <div className="text-[11px] text-white/70 border-l border-white/20 pl-2">
                    {current.location_label}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowUploader(true)}
                data-testid="stories-mine-add"
                className="shrink-0 h-12 px-4 rounded-full flex items-center gap-2 font-bold text-[13px] text-white"
                style={{
                  background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                  boxShadow: "0 10px 26px -8px rgba(255,122,26,0.65)",
                }}
              >
                <Plus className="h-4 w-4" /> Aggiungi
              </button>
            </div>
          )}
        </>
      )}

      {/* Uploader */}
      <AnimatePresence>
        {shouldShowUploader && (
          <div className="fixed inset-0 z-[2100] bg-black/80 flex flex-col">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-[2150] h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center"
              aria-label="Chiudi"
            >
              <X className="h-5 w-5" />
            </button>
            <StoryUploader
              quota={quota}
              onClose={() => {
                if (stories.length === 0) onClose();
                else setShowUploader(false);
              }}
              onCreated={() => {
                setShowUploader(false);
                load();
                toast.success("Storia pubblicata ✨");
              }}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  ), document.body);
}
